import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, errMsg } from "../lib/api";
import { Card, PageHeader, Btn, Pill, Input, Select, Textarea } from "../components/UI";
import { Copilot } from "../components/Copilot";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Check, Megaphone, Sparkles, Lock } from "lucide-react";

const OBJECTIVES = [
  "Awareness", "Website traffic", "Store visits", "Lead generation", "App installs",
  "Conversions", "Retargeting", "Competitor conquesting", "Loyalty activation",
  "Political / public affairs", "Event promotion", "Movie promotion",
  "Restaurant foot traffic", "Retail traffic"
];
const BILLING = ["CPM", "CPC", "CPA", "Cost per visit", "Flat package", "Monthly retainer", "Hybrid"];
const CHANNELS = [
  "Display", "Mobile", "Native", "Online video", "Connected TV", "Streaming audio",
  "Podcast audio", "DOOH", "Radio", "Social extension", "Search retargeting",
  "Owned media", "Loyalty placement"
];
const INVENTORY = [
  "Direct SSP", "Adlib", "The Trade Desk", "DV360",
  "GroundTruth", "Simpli.fi", "Direct publisher deal", "Private marketplace",
  "Programmatic guaranteed", "Owned media", "Radio inventory", "Manual booking"
];
const BID_STRATEGIES = [
  "Lowest cost", "Target CPM", "Target CPC", "Target CPA",
  "Target cost per visit", "Maximize reach", "Maximize store visits",
  "Maximize video completions", "Maximize household reach"
];

const STEPS = ["Basics", "Channels", "Inventory", "Targeting", "Creatives", "Budget & Bid", "Review"];

export default function CampaignBuilder() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [step, setStep] = useState(0);
  const [clients, setClients] = useState([]);
  const [creatives, setCreatives] = useState([]);
  const [audiences, setAudiences] = useState([]);
  const [polygons, setPolygons] = useState([]);
  const [registry, setRegistry] = useState([]);
  const [form, setForm] = useState({
    name: "", client_id: "", objective: "Awareness",
    start_date: "", end_date: "",
    total_budget: 10000, daily_budget: 300,
    timezone: "America/Chicago", category: "Standard",
    billing_model: "CPM", margin: 0.20, status: "Draft",
    channels: [], inventory_sources: [],
    targeting: { dma: "", radius_miles: 5, zip: "", state: "" },
    creatives: [], audiences: [], polygons: [],
    cpm_bid: 5, max_bid: 10, bid_strategy: "Lowest cost",
    frequency_cap: 3, political_disclaimer: "",
  });

  useEffect(() => {
    (async () => {
      const [cl, cr, au, pg, ch, pf] = await Promise.all([
        api.get("/clients"), api.get("/creatives"),
        api.get("/audiences"), api.get("/polygons"),
        api.get("/channels").catch(() => ({ data: [] })),
        api.get("/subscriptions/features").catch(() => ({ data: { gated: false } })),
      ]);
      setClients(cl.data); setCreatives(cr.data); setAudiences(au.data); setPolygons(pg.data);
      const feat = pf.data || {};
      const planBlocked = new Set(feat.gated ? (feat.blocked_channel_labels || []) : []);
      setRegistry((ch.data || []).filter(c => c.active !== false).map(c => ({ ...c, plan_blocked: planBlocked.has(c.label) })));
      if (id && id !== "new") {
        const { data } = await api.get(`/campaigns/${id}`);
        setForm({ ...form, ...data });
      }
    })();
    // eslint-disable-next-line
  }, [id]);

  const politicalNow = form.objective === "Political / public affairs" || form.category === "Political";
  useEffect(() => {
    if (!politicalNow || !registry.length) return;
    const blockedLabels = registry.filter(c => c.political_blocked).map(c => c.label);
    setForm(f => (
      f.channels.some(c => blockedLabels.includes(c))
        ? { ...f, channels: f.channels.filter(c => !blockedLabels.includes(c)) }
        : f
    ));
  }, [politicalNow, registry]);

  const toggleArr = (key, value) => {
    setForm(f => ({
      ...f,
      [key]: f[key].includes(value) ? f[key].filter(v => v !== value) : [...f[key], value]
    }));
  };

  const handleSave = async (statusOverride) => {
    const payload = {
      ...form,
      total_budget: parseFloat(form.total_budget) || 0,
      daily_budget: parseFloat(form.daily_budget) || 0,
      cpm_bid: parseFloat(form.cpm_bid) || 0,
      max_bid: parseFloat(form.max_bid) || 0,
      margin: parseFloat(form.margin) || 0,
      frequency_cap: parseInt(form.frequency_cap) || 0,
      status: statusOverride || form.status,
    };
    if (id && id !== "new") {
      try {
        await api.put(`/campaigns/${id}`, payload);
      } catch (e) {
        toast.error(errMsg(e, "Could not save campaign"));
        return;
      }
    } else {
      try {
        await api.post("/campaigns", payload);
      } catch (e) {
        toast.error(errMsg(e, "Could not save campaign"));
        return;
      }
    }
    navigate("/campaigns");
  };

  const isPolitical = form.objective === "Political / public affairs" || form.category === "Political";

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto">
      <PageHeader
        eyebrow={id && id !== "new" ? "Pro Mode — Edit Campaign" : "Pro Mode — Campaign Builder"}
        title={form.name || "New campaign"}
        subtitle="Advanced seven-step builder: channels, inventory, targeting, creatives, budget and compliance — full control."
        action={
          <div className="flex gap-2">
            <Btn variant="secondary" onClick={() => navigate("/campaigns")}>Cancel</Btn>
            <Btn variant="secondary" onClick={() => handleSave("Draft")} data-testid="save-draft-btn">Save draft</Btn>
          </div>
        }
      />

      {form.agent_session_id && (
        <div className="mb-4 rounded-md border border-cyan-400/25 bg-cyan-400/5 px-4 py-2.5 flex items-center justify-between" data-testid="ai-built-banner">
          <span className="text-xs text-cyan-200 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> This campaign was built with the TotalReach Campaign Agent — both views edit the same campaign.</span>
          <button onClick={() => navigate(`/campaign-agent/${form.agent_session_id}`)} className="text-xs text-cyan-300 hover:underline whitespace-nowrap" data-testid="return-simple-btn">Return to Simple View</button>
        </div>
      )}

      {/* Stepper */}
      <Card className="mb-6" padded={false}>
        <div className="flex overflow-x-auto" data-testid="builder-stepper">
          {STEPS.map((s, i) => (
            <button
              key={s}
              onClick={() => setStep(i)}
              data-testid={`step-${i}`}
              className={`flex-1 min-w-[110px] px-4 py-3 text-xs uppercase tracking-wider font-medium border-b-2 transition-colors ${
                i === step
                  ? "border-cyan-400 text-cyan-300 bg-cyan-400/5"
                  : i < step
                  ? "border-cyan-400/30 text-slate-300"
                  : "border-transparent text-slate-500 hover:text-slate-300"
              }`}
            >
              <span className="mr-1.5">{i + 1}.</span> {s}
            </button>
          ))}
        </div>
      </Card>

      <Card>
        {/* Step 1: Basics */}
        {step === 0 && (
          <div className="grid grid-cols-2 gap-4" data-testid="step-basics">
            <Input label="Campaign name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="col-span-2" data-testid="campaign-name-input" />
            <Select label="Client" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="campaign-client-select">
              <option value="">Select client…</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.business_name}</option>)}
            </Select>
            <Select label="Objective" value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value, category: e.target.value === "Political / public affairs" ? "Political" : "Standard" })} data-testid="campaign-objective-select">
              {OBJECTIVES.map(o => <option key={o}>{o}</option>)}
            </Select>
            <Input label="Start date" type="date" value={form.start_date?.substring(0, 10) || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            <Input label="End date" type="date" value={form.end_date?.substring(0, 10) || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
            <Input label="Total budget (USD)" type="number" value={form.total_budget} onChange={(e) => setForm({ ...form, total_budget: e.target.value })} />
            <Input label="Daily budget (USD)" type="number" value={form.daily_budget} onChange={(e) => setForm({ ...form, daily_budget: e.target.value })} />
            <Select label="Billing model" value={form.billing_model} onChange={(e) => setForm({ ...form, billing_model: e.target.value })}>
              {BILLING.map(b => <option key={b}>{b}</option>)}
            </Select>
            <Input
              label="Margin (0–1)"
              type="number" step="0.05" min="0" max="1"
              value={form.margin}
              onChange={(e) => setForm({ ...form, margin: e.target.value })}
              data-testid="campaign-margin-input"
              hint={<span data-testid="margin-hint">
                Your markup as a decimal: {((parseFloat(form.margin) || 0) * 100).toFixed(0)}% of budget kept as margin — ${((parseFloat(form.total_budget) || 0) * (parseFloat(form.margin) || 0)).toLocaleString(undefined, { maximumFractionDigits: 0 })} margin · ${((parseFloat(form.total_budget) || 0) * (1 - (parseFloat(form.margin) || 0))).toLocaleString(undefined, { maximumFractionDigits: 0 })} working media.{" "}
                <a href="/help?q=margin" className="text-cyan-300 hover:underline">What's this?</a>
              </span>}
            />
            <Input label="Timezone" value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} />
            {isPolitical && (
              <Textarea label="Required political disclaimer" value={form.political_disclaimer || ""} onChange={(e) => setForm({ ...form, political_disclaimer: e.target.value })} className="col-span-2" />
            )}
          </div>
        )}

        {/* Step 2: Channels */}
        {step === 1 && (
          <div data-testid="step-channels">
            <div className="text-sm text-slate-400 mb-4">Select all channels for this campaign. You can refine per-channel budget later.</div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
              {(registry.length ? registry : CHANNELS.map(c => ({ key: c, label: c }))).map(ch => {
                const policyBlocked = politicalNow && ch.political_blocked;
                const planLocked = !policyBlocked && ch.plan_blocked;
                const blocked = policyBlocked || planLocked;
                const active = form.channels.includes(ch.label);
                return (
                  <button key={ch.label}
                    onClick={() => { if (planLocked) navigate("/billing"); else if (!blocked) toggleArr("channels", ch.label); }}
                    data-testid={`channel-${ch.label}`}
                    disabled={policyBlocked} title={policyBlocked ? ch.blocked_reason : planLocked ? "Not included in your plan" : undefined}
                    className={`px-4 py-3 rounded-md border text-left text-sm transition-all ${
                      blocked
                        ? "bg-white/[0.01] border-white/5 text-slate-500 opacity-60" + (policyBlocked ? " cursor-not-allowed" : " cursor-pointer")
                        : active
                        ? "bg-gradient-to-r from-cyan-400/20 to-blue-500/10 border-cyan-400/50 text-white"
                        : "bg-white/[0.02] border-white/10 text-slate-300 hover:border-white/20"
                    }`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="flex items-center gap-2 flex-wrap">
                        {blocked && <Lock className="w-3.5 h-3.5 text-slate-500 shrink-0" />}
                        {ch.label}
                        {ch.is_new && !blocked && (
                          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-cyan-400/15 text-cyan-300 border border-cyan-400/25">New</span>
                        )}
                        {planLocked && (
                          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-400/15 text-amber-300 border border-amber-400/25" data-testid={`channel-upgrade-${ch.key}`}>Upgrade</span>
                        )}
                        {ch.execution_mode === "managed_handoff" && !blocked && (
                          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/5 text-slate-400 border border-white/10">Managed</span>
                        )}
                      </span>
                      {active && !blocked && <Check className="w-4 h-4 text-cyan-300 shrink-0" />}
                    </div>
                    {policyBlocked && (
                      <div className="text-[10px] text-amber-300/80 mt-1.5 leading-snug" data-testid={`channel-blocked-reason-${ch.key}`}>
                        {ch.blocked_reason}
                      </div>
                    )}
                    {planLocked && (
                      <div className="text-[10px] text-amber-300/80 mt-1.5 leading-snug">Not in your plan — click to view upgrade options.</div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 3: Inventory */}
        {step === 2 && (
          <div data-testid="step-inventory">
            <div className="text-sm text-slate-400 mb-4">Choose where this campaign will execute. Mix direct SSP, DSP connectors, and owned media.</div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
              {INVENTORY.map(src => {
                const active = form.inventory_sources.includes(src);
                return (
                  <button key={src} onClick={() => toggleArr("inventory_sources", src)} data-testid={`inventory-${src}`}
                    className={`px-4 py-3 rounded-md border text-left text-sm transition-all ${
                      active
                        ? "bg-gradient-to-r from-cyan-400/20 to-blue-500/10 border-cyan-400/50 text-white"
                        : "bg-white/[0.02] border-white/10 text-slate-300 hover:border-white/20"
                    }`}>
                    <div className="flex items-center justify-between">
                      <span>{src}</span>
                      {active && <Check className="w-4 h-4 text-cyan-300" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 4: Targeting */}
        {step === 3 && (
          <div className="grid grid-cols-2 gap-4" data-testid="step-targeting">
            <Input label="DMA" value={form.targeting.dma || ""} onChange={(e) => setForm({ ...form, targeting: { ...form.targeting, dma: e.target.value } })} placeholder="Dallas-Ft. Worth" />
            <Input label="State" value={form.targeting.state || ""} onChange={(e) => setForm({ ...form, targeting: { ...form.targeting, state: e.target.value } })} placeholder="TX" />
            <Input label="ZIP" value={form.targeting.zip || ""} onChange={(e) => setForm({ ...form, targeting: { ...form.targeting, zip: e.target.value } })} />
            <Input label="Radius (miles)" type="number" value={form.targeting.radius_miles || ""} onChange={(e) => setForm({ ...form, targeting: { ...form.targeting, radius_miles: parseFloat(e.target.value) } })} />

            <div className="col-span-2">
              <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-2">Audiences</div>
              <div className="flex flex-wrap gap-2">
                {audiences.length === 0 && <div className="text-sm text-slate-500">No audiences yet. Build one under Audiences.</div>}
                {audiences.map(a => (
                  <button key={a.id} onClick={() => toggleArr("audiences", a.id)}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                      form.audiences.includes(a.id) ? "bg-cyan-400/20 text-cyan-300 border-cyan-400/40" : "bg-white/5 border-white/10 text-slate-300 hover:border-white/20"
                    }`}>
                    {a.name} <span className="text-slate-500">· {(a.estimated_reach || 0).toLocaleString()}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="col-span-2">
              <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-2">Polygons / Geofences</div>
              <div className="flex flex-wrap gap-2">
                {polygons.length === 0 && <div className="text-sm text-slate-500">No polygons. Draw one in Geo Intelligence.</div>}
                {polygons.map(p => (
                  <button key={p.id} onClick={() => toggleArr("polygons", p.id)}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                      form.polygons.includes(p.id) ? "bg-cyan-400/20 text-cyan-300 border-cyan-400/40" : "bg-white/5 border-white/10 text-slate-300 hover:border-white/20"
                    }`}>
                    {p.name} · <span className="text-slate-500">{p.type}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Creatives */}
        {step === 4 && (
          <div data-testid="step-creatives">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm text-slate-400">Attach creatives to this campaign. Generate AI banners in the Creative Studio.</div>
              <Btn variant="secondary" onClick={() => navigate("/creative-studio")}>
                <Sparkles className="w-4 h-4" /> AI Creative Studio
              </Btn>
            </div>
            {creatives.length === 0 ? (
              <div className="text-center py-10 text-sm text-slate-500">No creatives uploaded yet.</div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {creatives.map(cr => {
                  const active = form.creatives.includes(cr.id);
                  return (
                    <button key={cr.id} onClick={() => toggleArr("creatives", cr.id)}
                      className={`rounded-md border overflow-hidden text-left ${active ? "border-cyan-400" : "border-white/10 hover:border-white/30"}`}>
                      <div className="aspect-video bg-slate-900 flex items-center justify-center overflow-hidden">
                        {cr.asset_base64 || cr.asset_url ? (
                          <img src={cr.asset_base64 || cr.asset_url} alt={cr.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="text-slate-600 text-xs">{cr.format}</div>
                        )}
                      </div>
                      <div className="p-2.5">
                        <div className="text-xs font-medium text-white truncate">{cr.name}</div>
                        <div className="text-[10px] text-slate-500 mt-0.5">{cr.format} · {cr.approval_status}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Step 6: Budget & Bid */}
        {step === 5 && (
          <div className="grid grid-cols-2 gap-4" data-testid="step-budget">
            <Input label="CPM bid (USD)" type="number" step="0.10" value={form.cpm_bid} onChange={(e) => setForm({ ...form, cpm_bid: e.target.value })} />
            <Input label="Max bid (USD)" type="number" step="0.10" value={form.max_bid} onChange={(e) => setForm({ ...form, max_bid: e.target.value })} />
            <Select label="Bid strategy" value={form.bid_strategy} onChange={(e) => setForm({ ...form, bid_strategy: e.target.value })}>
              {BID_STRATEGIES.map(b => <option key={b}>{b}</option>)}
            </Select>
            <Input label="Frequency cap (per user per day)" type="number" value={form.frequency_cap} onChange={(e) => setForm({ ...form, frequency_cap: e.target.value })} />
            <div className="col-span-2 grid grid-cols-3 gap-3 mt-2">
              <div className="rounded-md border border-white/10 p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Estimated media cost</div>
                <div className="font-display text-xl font-bold mt-1">${(form.total_budget * (1 - form.margin)).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
              </div>
              <div className="rounded-md border border-white/10 p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Platform markup</div>
                <div className="font-display text-xl font-bold mt-1 brand-gradient">${(form.total_budget * form.margin).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
              </div>
              <div className="rounded-md border border-white/10 p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Est. impressions @ CPM</div>
                <div className="font-display text-xl font-bold mt-1">{form.cpm_bid > 0 ? Math.round((form.total_budget / form.cpm_bid) * 1000).toLocaleString() : "—"}</div>
              </div>
            </div>
          </div>
        )}

        {/* Step 7: Review */}
        {step === 6 && (
          <div data-testid="step-review">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <ReviewRow label="Name" value={form.name || "—"} />
              <ReviewRow label="Client" value={clients.find(c => c.id === form.client_id)?.business_name || "—"} />
              <ReviewRow label="Objective" value={form.objective} />
              <ReviewRow label="Billing model" value={form.billing_model} />
              <ReviewRow label="Budget" value={`$${form.total_budget} total · $${form.daily_budget}/day`} />
              <ReviewRow label="Margin" value={`${(form.margin * 100).toFixed(0)}%`} />
              <ReviewRow label="Channels" value={form.channels.join(", ") || "—"} />
              <ReviewRow label="Inventory" value={form.inventory_sources.join(", ") || "—"} />
              <ReviewRow label="Creatives" value={form.creatives.length + " attached"} />
              <ReviewRow label="Audiences" value={form.audiences.length + " segments"} />
              <ReviewRow label="Polygons" value={form.polygons.length + " geofences"} />
              <ReviewRow label="Frequency cap" value={form.frequency_cap + "/day"} />
              {isPolitical && (
                <div className="col-span-2 rounded-md border border-amber-400/30 bg-amber-400/5 p-3 text-sm">
                  <div className="font-semibold text-amber-300 mb-1">⚖ Political compliance required</div>
                  <div className="text-slate-300">Disclaimer: <em>{form.political_disclaimer || "Not set"}</em></div>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <Btn variant="secondary" onClick={() => handleSave("Draft")} data-testid="review-save-draft">Save as draft</Btn>
              <Btn onClick={() => handleSave(isPolitical ? "Pending approval" : "Active")} data-testid="review-launch-btn">
                <Megaphone className="w-4 h-4" />
                {isPolitical ? "Submit for compliance review" : "Launch campaign"}
              </Btn>
            </div>
          </div>
        )}
      </Card>

      {/* Footer nav */}
      <div className="mt-6 flex items-center justify-between">
        <Btn variant="secondary" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} data-testid="prev-step-btn">
          <ChevronLeft className="w-4 h-4" /> Back
        </Btn>
        <div className="text-xs text-slate-500">Step {step + 1} of {STEPS.length}</div>
        <Btn onClick={() => setStep(Math.min(STEPS.length - 1, step + 1))} disabled={step === STEPS.length - 1} data-testid="next-step-btn">
          Next <ChevronRight className="w-4 h-4" />
        </Btn>
      </div>
      <Copilot context={form} />
    </div>
  );
}

const ReviewRow = ({ label, value }) => (
  <div className="border-b border-white/5 py-2">
    <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="text-white text-sm mt-0.5 truncate">{value}</div>
  </div>
);
